import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group } from "@shared/models/group"
import { handleError } from "cc-frontend/error-handling"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import groupViewAction from "cc-frontend/lib/actions/group/GROUP_VIEW"
import type Router from "cc-frontend/router"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import { isGroupMembershipGroup } from "cc-frontend/utils/group"
import { use } from "ember-could-get-used-to-this"
import * as R from "remeda" // tree-shaking supported!
import { tracked } from "tracked-built-ins"

interface Signature {
  Args: {
    group: Group
  }
}

export default class SchoolNetworkShow extends Component<Signature> {
  @service declare dialog: Dialog
  @service declare rpc: Rpc
  @service declare router: Router
  @service declare dispatcher: Dispatcher
  @service declare session: Session

  @tracked isCreatingSchool = false
  @tracked nameOfNewSchool: null | string = null

  get schoolIds() {
    return R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(isGroupMembershipGroup),
      R.filter((membership) => membership.attributes.usedAs === "school"),
      R.map((membership) => get(membership.attributes, "groupId"))
    )
  }

  @use schools = new FindManyAltogether(() => ["group", this.schoolIds])

  @action
  addSchool() {
    this.dialog
      .blank("ask-for-school-title", {
        className: "bold-dialog",
        isCreatingSchool: false,
        nameOfNewSchool: null,
        fromNetworkPage: true,
      })
      .then((nameOfNewSchool: string) => {
        this.isCreatingSchool = true
        this.rpc
          .group_create_school_in_network({
            parentGroupId: this.args.group.id,
            title: nameOfNewSchool,
          })
          .then((response) => {
            this.isCreatingSchool = false
            this.nameOfNewSchool = null
            if (response.responseOk) {
              this.router.transitionTo("new-group.show.edit.members", response.result.newGroupId)
            }
          })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  trackView = () => {
    if (this.isDestroyed || this.isDestroying) return

    this.dispatcher.dispatchPreparedAction(groupViewAction({ group: this.args.group }))
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "SchoolNetwork::Show": typeof SchoolNetworkShow
  }
}
